import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Autosaving form state for the long data-entry pages (Products, SOP editor).
 *
 * Incident 2026-10-09: the onboarding page kept edits in memory until "Save"
 * and remounted on every server change; a document upload (and the CofA/SDS
 * reading that follows) changed the server copy and wiped everything Chris had
 * typed. Now every edit is saved about a second after typing stops, and a
 * server refresh only updates fields that have no unsaved local edit.
 */

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/** Take server values for every field except those with an unsaved local edit. */
export function mergeServer<T extends object>(local: T, server: T, protectedKeys: ReadonlySet<keyof T>): T {
  const out = { ...local }
  for (const key of Object.keys(server) as (keyof T)[]) {
    if (!protectedKeys.has(key)) out[key] = server[key]
  }
  return out
}

/** True when the error is the server refusing the data (no point retrying the same values). */
function isClientError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

export function useAutosaveForm<T extends object>(
  server: T,
  save: (values: Partial<T>) => Promise<unknown>,
  { delayMs = 1000, retryMs = 5000 }: { delayMs?: number; retryMs?: number } = {},
) {
  const [form, setForm] = useState<T>(server)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [hasPending, setHasPending] = useState(false)
  const pending = useRef<Partial<T>>({}) // edited, not yet sent
  const inflight = useRef<Partial<T>>({}) // sent, not yet confirmed
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveRef = useRef(save)
  const flushRef = useRef<() => Promise<void>>(async () => {})

  useEffect(() => {
    saveRef.current = save
  }, [save])

  // Server copy changed (refetch after upload, extraction, someone else's save):
  // adopt it for every field that isn't waiting to be saved.
  useEffect(() => {
    const protectedKeys = new Set([...Object.keys(pending.current), ...Object.keys(inflight.current)] as (keyof T)[])
     
    setForm((local) => mergeServer(local, server, protectedKeys))
  }, [server])

  const schedule = useCallback(
    (ms: number) => {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => void flushRef.current(), ms)
    },
    [],
  )

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current)
    const batch = pending.current
    if (!Object.keys(batch).length || Object.keys(inflight.current).length) return
    pending.current = {}
    inflight.current = batch
    setHasPending(false)
    setStatus('saving')
    try {
      await saveRef.current(batch)
      inflight.current = {}
      setError(null)
      if (Object.keys(pending.current).length) {
        setHasPending(true)
        schedule(delayMs)
      } else setStatus('saved')
    } catch (err) {
      // Put the batch back under anything typed since, so nothing is lost.
      pending.current = { ...batch, ...pending.current }
      inflight.current = {}
      setHasPending(true)
      setStatus('error')
      setError((err as Error).message)
      if (!isClientError(err)) schedule(retryMs)
    }
  }, [delayMs, retryMs, schedule])

  useEffect(() => {
    flushRef.current = flush
  }, [flush])

  const set = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setForm((f) => ({ ...f, [key]: value }))
      pending.current = { ...pending.current, [key]: value }
      setHasPending(true)
      schedule(delayMs)
    },
    [delayMs, schedule],
  )

  const setMany = useCallback(
    (values: Partial<T>) => {
      setForm((f) => ({ ...f, ...values }))
      pending.current = { ...pending.current, ...values }
      setHasPending(true)
      schedule(delayMs)
    },
    [delayMs, schedule],
  )

  const unsaved = status === 'saving' || status === 'error' || hasPending

  // Warn before closing the tab with edits that haven't reached the server.
  useEffect(() => {
    if (!unsaved) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [unsaved])

  // Save what's pending when the page is left inside the app.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
      void flushRef.current()
    },
    [],
  )

  return { form, set, setMany, status, error, flush, unsaved }
}
